// stores/genericStore.js
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useToast } from 'vue-toastification';
import apiClient from '@/axios';

export const createGenericStore = (storeName, apiEndpoint, defaultConfig = {}) => {
  return defineStore(storeName, () => {
    // STATE - همه چیز باید reactive باشد
    const itemsList = ref([]);
    const pagination = ref({
      total: 0,
      per_page: 20,
      current_page: 1,
      last_page: 1,
    });
    const searchQuery = ref("");
    const filters = ref([]);
    const lastSearchQuery = ref(""); // تبدیل به reactive
    const debounceTimer = ref(null); // تبدیل به reactive
    const isLoading = ref(false); // اضافه کردن state برای loading

    const toast = useToast();

    // ACTIONS
    async function fetchItems(page = 1) {
      isLoading.value = true;
      try {
        const requestBody = {
          filters: filters.value,
          page,
          perPage: pagination.value.per_page,
          search: searchQuery.value,
          searchColumn: defaultConfig.searchColumns || ['name'],
          orderBy: defaultConfig.orderBy || 'id DESC'
        };

        console.log(`[${storeName}] Fetching page ${page} with query:`, searchQuery.value);

        const response = await apiClient.post(apiEndpoint, requestBody);

        if (response.data && Array.isArray(response.data.result)) {
          if (page === 1) {
            itemsList.value = response.data.result;
          } else {
            itemsList.value = [...itemsList.value, ...response.data.result];
          }

          const total = response.data.totalCount || 0;
          pagination.value = {
            total: total,
            current_page: response.data.currentPage || page,
            per_page: response.data.perPage || pagination.value.per_page,
            last_page: response.data.lastPage || 1
          };

          console.log(`[${storeName}] Fetched ${response.data.result.length} items, total: ${itemsList.value.length}`);
        } else {
          console.error(`[${storeName}] Invalid API response:`, response.data);
          if (page === 1) {
            itemsList.value = [];
          }
          toast.error(`خطا در دریافت اطلاعات`);
        }
      } catch (error) {
        console.error(`[${storeName}] Error fetching:`, error);
        if (page === 1) {
          itemsList.value = [];
        }
        toast.error("خطا در ارتباط با سرور");
      } finally {
        isLoading.value = false;
      }
    }

    async function searchItems(query, searchColumns = defaultConfig.searchColumns) {
      if (query === lastSearchQuery.value) {
        console.log(`[${storeName}] Skipping duplicate search query:`, query);
        return;
      }

      searchQuery.value = query;
      lastSearchQuery.value = query;

      // Clear previous debounce timer
      if (debounceTimer.value) {
        clearTimeout(debounceTimer.value);
      }

      // Use debounce for search
      debounceTimer.value = setTimeout(async () => {
        await fetchItems(1);
      }, 300);
    }

    // متد جدید برای بارگذاری داده‌های اولیه بدون جستجو
    async function loadInitialData() {
      searchQuery.value = "";
      lastSearchQuery.value = "";

      if (debounceTimer.value) {
        clearTimeout(debounceTimer.value);
      }

      await fetchItems(1);
    }

    function setFilters(newFilters) {
      filters.value = newFilters;
    }

    function clearFilters() {
      filters.value = [];
    }

    function resetSearchState() {
      lastSearchQuery.value = "";
      searchQuery.value = "";

      if (debounceTimer.value) {
        clearTimeout(debounceTimer.value);
        debounceTimer.value = null;
      }
    }

    async function loadNextPage() {
      if (pagination.value.current_page < pagination.value.last_page) {
        const nextPage = pagination.value.current_page + 1;
        await fetchItems(nextPage);
      }
    }

    // متد برای پاکسازی تایمرها
    function cleanup() {
      if (debounceTimer.value) {
        clearTimeout(debounceTimer.value);
        debounceTimer.value = null;
      }
    }
    function findItemById(id) {
      return itemsList.value.find(item => item[defaultConfig.valueField || 'id'] === id);
    }
    return {
      // State
      itemsList,
      pagination,
      searchQuery,
      filters,
      isLoading,

      // Actions
      fetchItems,
      searchItems,
      loadInitialData,
      setFilters,
      clearFilters,
      resetSearchState,
      loadNextPage,
      cleanup,
      findItemById
    };
  });
}